// TRAC-1152: the phone chat's message box rides the on-screen keyboard like a native composer.
//
// The site around /embed keeps its chat window full screen and tells us how many px of the
// window's bottom the keyboard covers (TRACI_KB_COVER, embedKeyboardBridge.ts). Measured on
// iOS 26.1 (kb-ride-2 to kb-ride-5, 5 and 6 Oct 2026):
//   - the page hears of the keyboard once, 10 to 120 ms after it starts, with its final value;
//   - while the keyboard slides the page often draws nothing for 200 to 600 ms, so anything
//     laid out frame by frame lurches, and only a compositor transform keeps moving;
//   - with the keyboard up iPhone Safari draws nothing new below the visible area, so any
//     height change leaves grey where the chat used to be.
// So on iPhone and iPad the chat keeps its full height (HOLD) and only the message box and the
// list's content move, as transforms:
//   open:  on the keyboard's own curve, a critically damped spring 1 - (1 + t/40 ms) e^(-t/40 ms)
//          (fitted on the iPhone 17 Pro sim and Dylan's iPhone 13), started on the first drawn frame.
//   close: the keyboard drops on 1 - (1 - t/130 ms)^1.5. Done (the system takes the focus) starts
//          the slide 15 ms after focus leaves the box. A send (the chat's own blur) starts it on the
//          next frame the chat draws: iOS starts the keyboard down with the frame that shows the sent
//          message, while the site's "0" only comes once the keyboard is already down (kb-send-close,
//          7 Oct 2026: the box hung 117 to 265 ms, then jumped).
//   white: a layer in the chat's own background from the list's end down under the box, so nothing
//          undrawn shows under it and no text shows through the box, its ring or the band around it
//          (the list runs on behind the held box), plus a copy of the box (background, border, ring)
//          on top of that layer (kb-halo-fix) and a copy of the list's bottom fade above the box (the
//          list's own fade stays at the bottom of the full-height list, behind the keyboard).
// Android keeps the window's height change (no grey rule there) with the same transform slide.
// The drag lock (all phones): a drag that does not start on something scrollable never moves the
// chat frame or the page behind it; iOS 26 ignores the frame's touch-action for that, so the
// drag itself is cancelled.
//
// Not met, by design: the box within 8 pt of the keyboard in every frame. The page hears the
// keyboard late and no earlier signal exists on iOS. TRAC-1174 tried starting the slide on the tap
// toward the last height: the open median fell (41 to 33 pt) but the worst frame doubled (76 to
// 163 pt) and the box hid behind the keyboard for up to 5 frames, because the keyboard itself starts
// 270 ms to 1.3 s after the focus; so the slide still starts on the site's value.
//
// Switches: the remote setting phone_kb_ride ({ ride, lock }, read by EmbedPage), and for local
// testing window.TRACI_KB_HOLD = false (ride) and window.TRACI_KB_LOCK = false (lock), read at install.

import { isTextField, KB_ATTR, KB_COVER_VAR } from './embedKeyboardBridge';
import { KB_APP_BLUR_EVENT, PHONE_MEDIA_QUERY } from '../utils/phoneKeyboard';
/** Marks the elements the ride moves; set in the chat's markup, never hashed class names. */
export const KB_PART_ATTR = 'data-traci-kb-part';
/** On the list wrapper while it shows its bottom fade (a conversation, not the opening screen). */
export const KB_FADE_ATTR = 'data-traci-kb-fade';
/** On <html> while the ride draws its own fade above the box (the list's fade is hidden then). */
export const KB_RIDE_FADE_ATTR = 'data-traci-kb-ride-fade';
/** TRAC-1176: on <html> while HOLD lifts the box: how far, so the scroll arrow and New message pill ride with it. */
export const KB_HELD_VAR = '--traci-kb-held';
/** TRAC-1174: on <html>, how many px of the chat's bottom (the legal line) run under the keyboard on a phone held sideways. */
export const KB_TUCK_VAR = '--traci-kb-tuck';
/** A phone held sideways (the frame under 500px high and wider than any phone upright, so a keyboard-shrunk portrait
 * frame never matches); the same query gates the compact header in the CSS. */
export const LANDSCAPE_PHONE_QUERY = '(orientation: landscape) and (min-width: 540px) and (max-height: 500px) and (pointer: coarse)';
/**
 * TRAC-1176: fired (bubbling, detail `{ lift }`) on the list just before HOLD's open slide, after KB_ATTR and KB_HELD_VAR
 * are set. The chat's scroll owner sizes a turn spacer to the room above the lifted box and scrolls back by the lift in
 * that same task; the ride then holds the list content up by however far scrollTop moved, so nothing moves on screen.
 */
export const KB_LIFT_EVENT = 'traci-kb-lift';

const KB_UP_MIN_PX = 80;
const OPEN_A = 40, CLOSE_D = 130, CLOSE_N = 1.5;
const CLOSE_WAIT_SYS = 15;    // Done: the keyboard starts down about this long after focusout
const CLOSE_LEAD = 20;        // a close the page hears of first (the site's "0" with no blur): start that far in
const TAU = 57, CATCH = 0.5, CLOSE_MS = 120, SYS_LEAD = 50;   // the height path (Android)
const SMALL = 40;             // cover changes under this (toolbar, QuickType) just apply
const CLOSE = [0, 0.005, 0.1, 0.25, 0.32, 0.47, 0.59, 0.7, 0.83, 0.9, 1];   // keyboard drop, one value per 17 ms
const FADE_PX = 40;           // the list's bottom fade (ChatInterface.module.css .messagesWrapper::after)
const TUCK_GAP = 2;           // sideways: the box ends this far above the keyboard

export interface RideOptions {
  /** The ride itself; false keeps only the drag lock (the cover then just sets the plain layout). Default true. */
  ride?: boolean;
  /** HOLD: keep the full height and slide transforms (iPhone, iPad). Default: from the user agent. */
  hold?: boolean;
  /** The drag lock. Default true. */
  lock?: boolean;
}

export interface Ride {
  /** A cover value from the site (already origin-checked and clamped). */
  cover(px: number): void;
  /** Drop every transform and layer and fall back to the plain layout at `cover` (UI ready, hidden tab). */
  reset(): void;
  /** The latest cover, so a reinstall (remote switch) can pick up a keyboard that is already up. */
  last(): number;
  uninstall(): void;
}

interface Parts {
  w: HTMLElement;
  sc: HTMLElement;
  box: HTMLElement[];
  body: HTMLElement[];
  atEnd: boolean;
}

interface Run {
  anims: Animation[];
  els: HTMLElement[];
  was: string[];
  timer?: ReturnType<typeof setTimeout>;
}

type KbWindow = Window & { TRACI_KB_HOLD?: unknown; TRACI_KB_LOCK?: unknown };

export function isIosTouch(win: Window): boolean {
  const n = win.navigator;
  return /iP(hone|od|ad)/.test(n.userAgent) || (n.platform === 'MacIntel' && n.maxTouchPoints > 1);
}

/** True for a phone-sized touch screen inside a frame: the only place the ride and the lock run. */
export function isFramedPhone(win: Window): boolean {
  try {
    return !!win.parent && win.parent !== win && typeof win.matchMedia === 'function' && win.matchMedia(PHONE_MEDIA_QUERY).matches;
  } catch {
    return false;
  }
}

/** The keyboard's rise, one value every 8 ms, until within 0.2% of the top. */
function spring(): { at: number[]; ms: number } {
  const out: number[] = [];
  for (let t = 0, p = 0; p < 0.998; t += 8) { p = 1 - (1 + t / OPEN_A) * Math.exp(-t / OPEN_A); out.push(p); }
  out.push(1);
  return { at: out, ms: (out.length - 1) * 8 };
}
const DROP = ((): number[] => { const out: number[] = []; for (let t = 0; t < CLOSE_D; t += 8) out.push(1 - Math.pow(1 - t / CLOSE_D, CLOSE_N)); out.push(1); return out; })();
const DROP_MS = (DROP.length - 1) * 8;
/** Progress from k0 to 1 along 1 - e^(-t/TAU), one value every 10 ms (the height path). */
function curve(k0: number): { at: number[]; ms: number } {
  const ms = Math.max(20, TAU * Math.log((1 - k0) / 0.004)), out: number[] = [];
  for (let t = 0; t < ms; t += 10) out.push(1 - (1 - k0) * Math.exp(-t / TAU));
  out.push(1);
  return { at: out, ms: out.length * 10 - 10 };
}

const kids = (el: Element): HTMLElement[] => Array.from(el.children) as HTMLElement[];

/** Install the ride (and the drag lock) on a framed phone; null anywhere else. */
export function installEmbedKeyboardRide(win: Window, opts: RideOptions = {}): Ride | null {
  if (!isFramedPhone(win)) return null;
  const kw = win as KbWindow;
  const gw = win as Window & typeof globalThis;
  const doc = win.document, de = doc.documentElement;
  const HOLD = (opts.hold ?? isIosTouch(win)) && kw.TRACI_KB_HOLD !== false;
  const LOCK = (opts.lock ?? true) && kw.TRACI_KB_LOCK !== false;
  const RIDE = opts.ride !== false;
  const now = (): number => win.performance.now();
  const css = (el: Element): CSSStyleDeclaration => win.getComputedStyle(el);
  const still = typeof win.matchMedia === 'function' && win.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const offs: Array<() => void> = [];
  const on = (t: EventTarget, type: string, fn: (e: never) => void, o?: AddEventListenerOptions | boolean): void => {
    t.addEventListener(type, fn as EventListener, o);
    offs.push(() => t.removeEventListener(type, fn as EventListener, o));
  };
  const timers = new Set<ReturnType<typeof setTimeout>>();
  const later = (fn: () => void, ms: number): ReturnType<typeof setTimeout> => {
    const id = setTimeout(() => { timers.delete(id); fn(); }, ms);
    timers.add(id);
    return id;
  };

  // Layers, all built from constants and computed styles (no message data reaches the DOM).
  const back = doc.createElement('style');   // the chat's background behind the whole frame
  const under = doc.createElement('div');    // the chat's background behind and below the moving box
  under.setAttribute('aria-hidden', 'true');
  const cap = doc.createElement('div');      // the box itself (background, border, ring) over `under`
  cap.setAttribute('aria-hidden', 'true');
  const fade = doc.createElement('div');     // the list's bottom fade, riding above the box
  fade.setAttribute('aria-hidden', 'true');
  let bg = '';

  let base = 0;          // the cover the layout has now
  let cur = 0, up = 0;   // the latest cover; the last open cover (refocus mid-close)
  let run: Run | null = null;
  let closingUntil = 0, swallowed = 0, leftAt = 0, closeAt = 0;
  // HOLD: the slide that ended, kept as plain transforms (box `held` px up, list content `hShift` up)
  let held = 0, hShift = 0, hp: Parts | null = null, follow = true;
  // Sideways (TRAC-1174) the held box rises `held - tk`: the legal line under it stays behind the keyboard
  let tk = 0;
  let turned = false;   // the phone turned while the keyboard was up: the next cover holds again (upright)
  let ro: ResizeObserver | null = null, mo: MutationObserver | null = null, mq = 0;
  let touching = 0, idleT: ReturnType<typeof setTimeout> | undefined;
  let touchedAt = -1e9, appBlurAt = -1e9, lastW = win.innerWidth, sendRaf = 0;

  function chatBackground(w: HTMLElement): string {
    for (let el: HTMLElement | null = w; el; el = el.parentElement) {
      const c = css(el).backgroundColor;
      if (c && c !== 'transparent' && !/rgba\([^)]*,\s*0\)$/.test(c)) return c;
    }
    return '#fff';
  }
  function paintBack(w: HTMLElement): void {
    const c = chatBackground(w);
    if (c === bg && back.isConnected) return;
    bg = c;
    back.textContent = `html,body{background:${c}}body::before{content:"";position:fixed;inset:0;background:${c};z-index:-1;transform:translateZ(0);pointer-events:none}`;
    if (!back.isConnected) (doc.head || de).appendChild(back);
  }

  function parts(): Parts | null {
    const w = doc.querySelector<HTMLElement>(`[${KB_PART_ATTR}="widget"]`);
    const sc = w && w.querySelector<HTMLElement>(`[${KB_PART_ATTR}="list"]`);
    const ta = w && w.querySelector('textarea');
    if (!w || !sc || !ta) return null;
    // the box and everything under it move: the list's ancestor that shares a parent with the box, and its later siblings
    let box: HTMLElement = sc;
    while (box.parentElement && box.parentElement !== w && !box.parentElement.contains(ta)) box = box.parentElement;
    if (!box.parentElement || !box.parentElement.contains(ta)) return null;
    const sibs = kids(box.parentElement), i = sibs.indexOf(box);
    const atEnd = sc.scrollHeight - sc.clientHeight - sc.scrollTop < 8;
    return { w, sc, box: sibs.slice(i + 1), body: kids(sc), atEnd };
  }

  function setCover(px: number): void {
    base = px;
    if (px > 0) de.style.setProperty(KB_COVER_VAR, px + 'px'); else de.style.removeProperty(KB_COVER_VAR);
    if (px >= KB_UP_MIN_PX) de.setAttribute(KB_ATTR, 'up'); else de.removeAttribute(KB_ATTR);
    const t = tuck(px >= KB_UP_MIN_PX ? parts() : null);
    if (t > 0) de.style.setProperty(KB_TUCK_VAR, t + 'px'); else de.style.removeProperty(KB_TUCK_VAR);
  }

  // A phone held sideways with the keyboard up leaves about 98 pt (iPhone Safari): too little for the
  // header, the box and the legal line under it. The legal line then goes under the keyboard (Josh: the
  // keyboard may cover it) and the box sits on the keyboard. How far: the chat's bottom less the box's.
  function tuck(p: Parts | null): number {
    if (!p || typeof win.matchMedia !== 'function' || !win.matchMedia(LANDSCAPE_PHONE_QUERY).matches) return 0;
    const ta = p.w.querySelector('textarea'), box = p.box.find((el) => !!ta && el.contains(ta));
    if (!box) return 0;
    return Math.max(0, Math.round(p.w.getBoundingClientRect().bottom - (box.getBoundingClientRect().bottom - offset(box)) - TUCK_GAP));
  }

  function offset(el: Element | undefined): number {
    if (!el) return 0;
    const t = css(el).transform;
    if (!t || t === 'none') return 0;
    try { return new DOMMatrixReadOnly(t).m42 || 0; } catch { return 0; }
  }

  // The box over the white (kb-halo-fix, kb-gray-opaque): `under` sits inside the box's stacking
  // context, so even at z-index -1 it paints over the box's own background, border and box-shadow.
  // A copy of those (sharp shadows only: the ring), appended after `under`, draws the box again on
  // top of it. The box's own background is see-through (0.92 white, blurred); over the white it is
  // solid, so the list's text no longer shows through the box or its ring (Dylan, 6 Oct 2026).
  function capFit(host: HTMLElement): void {
    const cs = css(host);
    const sharp: string[] = [];
    (cs.boxShadow || '').split(/,(?![^(]*\))/).forEach((s) => {
      if (/inset/.test(s)) return;
      const n = (s.replace(/(rgba?|hsla?|color)\([^)]*\)/g, '').match(/-?[\d.]+px/g) || []).map(parseFloat);   // x y blur spread
      if (n.length >= 2 && !n[2]) sharp.push(s.trim());
    });
    const side = (k: 'Top' | 'Right' | 'Bottom' | 'Left'): string =>
      `${cs.getPropertyValue(`border-${k.toLowerCase()}-width`)} ${cs.getPropertyValue(`border-${k.toLowerCase()}-style`)} ${cs.getPropertyValue(`border-${k.toLowerCase()}-color`)}`;
    cap.style.cssText = `position:absolute;box-sizing:border-box;pointer-events:none;z-index:-1;` +
      `top:-${cs.borderTopWidth};right:-${cs.borderRightWidth};bottom:-${cs.borderBottomWidth};left:-${cs.borderLeftWidth};` +
      `background:${cs.backgroundColor};border-top:${side('Top')};border-right:${side('Right')};border-bottom:${side('Bottom')};border-left:${side('Left')};` +
      `border-radius:${cs.borderTopLeftRadius} ${cs.borderTopRightRadius} ${cs.borderBottomRightRadius} ${cs.borderBottomLeftRadius};box-shadow:${sharp.join(',') || 'none'}`;
  }

  // The band between the list's end and the box (the box's margin, 4 px), as laid out without the held
  // lift. Capped, so a hidden list (YMMV, bottom 0) never turns the chat above the box white.
  const GAP_MAX = 12;
  function gapAbove(host: HTMLElement, p: Parts): number {
    return Math.min(GAP_MAX, Math.max(0, host.getBoundingClientRect().top - offset(host) - p.sc.getBoundingClientRect().bottom));
  }

  // The list's bottom fade, copied onto the box: the list's own is a sticky ::after at the bottom of
  // the full-height list, which HOLD leaves behind the keyboard, so the text ran up to the box with
  // no fade (Dylan, 6 Oct 2026). It sits in the gap above the box, spans the chat's width and moves
  // with the box on the same transforms.
  function fadeFit(host: HTMLElement, p: Parts, gap: number): void {
    if (!p.sc.hasAttribute(KB_FADE_ATTR)) { fade.remove(); de.removeAttribute(KB_RIDE_FADE_ATTR); return; }
    const hb = host.getBoundingClientRect(), wb = p.w.getBoundingClientRect();
    fade.style.cssText = `position:absolute;pointer-events:none;z-index:1;left:${wb.left - hb.left}px;width:${wb.width}px;` +
      `bottom:calc(100% + ${gap}px);height:${FADE_PX}px;background:linear-gradient(to bottom,transparent 0%,${bg || '#fff'} 100%)`;
    if (fade.parentNode !== host) host.appendChild(fade);
    de.setAttribute(KB_RIDE_FADE_ATTR, '');
  }

  function prep(p: Parts | null): void {   // before any slide: layers for what moves, white and ring under the box, the fade above
    if (!p) return;
    paintBack(p.w);
    p.box.concat(p.body).forEach((el) => { if (el.style.willChange !== 'transform') el.style.willChange = 'transform'; });
    const host = p.box[0];
    if (!host) return;
    if (under.parentNode !== host) {
      if (under.parentNode) (under.parentNode as HTMLElement).style.position = under.dataset.pos || '';
      under.dataset.pos = host.style.position;
      if (css(host).position === 'static') host.style.position = 'relative';
      host.appendChild(under);
    }
    // from the list's end (where the fade above the box ends) down: the band around the box is solid
    const gap = gapAbove(host, p);
    under.style.cssText = `position:absolute;left:-16px;right:-16px;top:-${gap + 1}px;height:calc(100% + ${gap + 1}px + 100vh);background:${bg};pointer-events:none;z-index:-1`;
    capFit(host);
    if (cap.parentNode !== host || !(under.compareDocumentPosition(cap) & 4)) host.appendChild(cap);   // after the white
    fadeFit(host, p, gap);
  }
  function unprep(): void {
    if (under.parentNode) { (under.parentNode as HTMLElement).style.position = under.dataset.pos || ''; under.remove(); }
    cap.remove(); fade.remove(); de.removeAttribute(KB_RIDE_FADE_ATTR);
    if (!held) setHeld(0);   // a lift that never reached HOLD (the app swapped a node, a width change)
    const p = parts();   // keyboard down: give the layers back (a long conversation's list is a big layer)
    if (p) p.box.concat(p.body).forEach((el) => { if (el.style.willChange === 'transform') el.style.willChange = ''; });
  }

  function stop(): void {
    if (!run) return;
    const r = run; run = null;
    clearTimeout(r.timer);
    r.anims.forEach((a) => a.cancel());
    r.els.forEach((el, i) => { el.style.transform = r.was[i]; });
  }

  // moves: [element, y per step], on one timeline of `ms` that started at t0 (performance.now()).
  // t0 false: left pending, so the browser starts it on the first frame it draws.
  // rest: the transform every element returns to when the slide stops (default: what it had before).
  function animate(moves: Array<[HTMLElement | undefined, number[]]>, ms: number, t0: number | false | null, done?: () => void, rest?: string): void {
    const mv = moves.filter((m): m is [HTMLElement, number[]] => !!m[0]);
    const me: Run = { anims: [], els: [], was: [] };
    run = me;
    mv.forEach(([el, ys]) => {
      me.els.push(el); me.was.push(rest ?? el.style.transform);
      el.style.transform = `translateY(${ys[0].toFixed(1)}px)`;   // drawn there even before the animation runs
      if (typeof el.animate !== 'function') return;
      const an = el.animate(ys.map((y) => ({ transform: `translateY(${y.toFixed(1)}px)` })), { duration: ms, easing: 'linear', fill: 'both' });
      // A set current time draws at once, even before WebKit gives the animation a start time (a stalled
      // frame still draws the first step); counted from t0 so a late handler does not trail. Not
      // startTime: the animation clock is stale after an idle stretch.
      if (t0 !== false) an.currentTime = t0 != null ? Math.max(-ms, Math.min(ms - 1, now() - t0)) : 0;
      me.anims.push(an);
    });
    const fin = (): void => {   // once, on finish or (backgrounded tab, no finish event) the timer
      if (run !== me) return;
      if (!me.els.every((el) => el.isConnected)) { stop(); unprep(); setCover(cur); return; }   // the app replaced a node mid-slide
      done?.();
    };
    if (me.anims[0]) { me.anims[0].onfinish = fin; me.timer = later(fin, ms + 600); } else fin();
  }

  function setHeld(px: number): void {   // TRAC-1176: the arrow/pill (outside what HOLD moves) take the box's lift
    if (px > 0) de.style.setProperty(KB_HELD_VAR, `${px}px`); else de.style.removeProperty(KB_HELD_VAR);
  }
  // HOLD: while the keyboard is up the box and list sit slid up as plain transforms; the layout never changes.
  function hApply(): void {
    if (!hp) return;
    hp.box.forEach((el) => { el.style.transform = `translateY(${-(held - tk)}px)`; });
    kids(hp.sc).forEach((el) => {
      if (el.style.willChange !== 'transform') el.style.willChange = 'transform';
      el.style.transform = hShift ? `translateY(${-hShift}px)` : '';
    });
  }
  /** How far the content must sit up so its end shows just above the box (only at its end, the chat's own rule). */
  function hEnd(sc: HTMLElement, lift: number): number {
    const top = sc.getBoundingClientRect().top;
    let C = 0;   // where the content really ends (scrollHeight is never below clientHeight)
    kids(sc).forEach((el) => { C = Math.max(C, el.getBoundingClientRect().bottom - offset(el) - top + sc.scrollTop); });
    if (sc.scrollHeight > sc.clientHeight + 1) C = Math.max(C, sc.scrollHeight);
    return Math.max(0, Math.min(lift, C - sc.scrollTop - sc.clientHeight + lift));
  }
  function watch(p: Parts): void {
    if (!ro && typeof gw.ResizeObserver === 'function') ro = new gw.ResizeObserver(() => hRefresh());
    if (ro) { ro.disconnect(); ro.observe(p.sc); kids(p.sc).forEach((el) => ro?.observe(el)); }
    if (!mo && typeof gw.MutationObserver === 'function') {
      mo = new gw.MutationObserver(() => { if (!mq) { mq = 1; win.requestAnimationFrame(() => { mq = 0; hRefresh(); }); } });
    }
    if (mo) { mo.disconnect(); mo.observe(p.w, { childList: true, subtree: true }); }
  }
  function unwatch(): void { ro?.disconnect(); mo?.disconnect(); }
  function hRefresh(): void {
    if (!held || !hp || run) return;
    const q = parts();   // the app re-renders the box area after a send: take the new nodes
    if (q && (q.sc !== hp.sc || q.box.some((el, i) => el !== hp?.box[i]))) {
      hp = { ...hp, box: q.box, sc: q.sc, w: q.w };
      watch(hp);
      prep(hp);
      if (!follow) hApply();   // the new box starts untransformed: lift it now, not only when following
    }
    if (!hp.sc.isConnected || !hp.box.every((el) => el.isConnected)) { hRelease(); return; }
    if (cap.parentNode === hp.box[0]) capFit(hp.box[0]);   // a class change (YMMV) restyles the box: copy it again
    const t = Math.min(held, tuck(hp));   // sideways: the legal line rewrapped, or the box grew
    if (t !== tk) { tk = t; setHeld(held - tk); hApply(); }
    if (follow) { hShift = hEnd(hp.sc, held - tk); hApply(); }
  }
  function hHold(p: Parts, to: number, shift: number): void {   // the slide ended: keep it as transforms
    held = to; hp = p; hShift = shift; follow = shift > 0 || p.atEnd;
    de.setAttribute(KB_ATTR, 'up');
    hApply();
    watch(p);
  }
  function hRelease(): void {   // fall back to the real layout (rotation, the app rebuilt the chat)
    if (!held) return;
    const p = hp, s = p ? p.sc.scrollTop : 0, sh = hShift, to = held;
    unwatch();
    if (p) { p.box.forEach((el) => { el.style.transform = ''; }); kids(p.sc).forEach((el) => { el.style.transform = ''; }); }
    held = 0; hp = null; hShift = 0; tk = 0; setHeld(0);
    setCover(to);
    if (p && sh) p.sc.scrollTop = s + sh;
  }

  function lift(to: number): void {   // open
    const p = parts();
    let done = 0, inner = 0;
    if (run && p) { done = -offset(p.box[0]); inner = -offset(p.body[0]); }   // a new value mid-slide (Android steps): go on from here
    if (HOLD && held && p && !run) { done = held - tk; inner = hShift; held = 0; hp = null; unwatch(); }
    stop();
    const t = HOLD ? Math.min(to, tuck(p)) : 0;   // sideways: rise less, the legal line stays under the keyboard
    const d = to - t - base;
    cur = up = to;
    if (!p || d <= 0 || still) { tk = 0; setCover(to); return; }
    tk = t;
    prep(p);
    const sc = p.sc, s = sc.scrollTop;
    // where the list ends up once the box is d higher: at its end it stays at its end (the chat's own rule); else it stays
    let shift = p.atEnd || inner > 0 ? Math.max(0, Math.min(d, sc.scrollHeight - (sc.clientHeight - d) - s)) : 0;
    let jump = 0;   // TRAC-1176: how far the scroll owner moved scrollTop at the start (the content is held that far up)
    if (HOLD) {
      de.setAttribute(KB_ATTR, 'up'); setHeld(to - tk);   // the arrow/pill take the box's lift (sideways: less the tuck)
      if (done === 0 && inner === 0) {
        sc.dispatchEvent(new (gw.CustomEvent || CustomEvent)(KB_LIFT_EVENT, { bubbles: true, detail: { lift: d } }));
        jump = Math.max(0, s - sc.scrollTop);
      }
      shift = jump > 0 ? jump : p.atEnd || inner > 0 ? hEnd(sc, d) : 0;
    }
    const k0 = done > 0 ? Math.min(0.99, done / d) : base === 0 ? (HOLD ? 0 : CATCH) : 0;
    const c = HOLD && k0 === 0 ? spring() : curve(k0);
    const moves: Array<[HTMLElement | undefined, number[]]> = p.box.map((el) => [el, c.at.map((k) => -d * k)]);
    if (shift > 0) p.body.forEach((el) => { moves.push([el, c.at.map((k) => -(jump + (shift - jump) * k))]); });
    animate(moves, c.ms, HOLD ? false : null, () => {
      if (HOLD) { const r = run; run = null; if (r) { clearTimeout(r.timer); } hHold(p, to, shift); r?.anims.forEach((a) => a.cancel()); return; }
      stop();
      setCover(to);
      if (shift > 0) sc.scrollTop = s + shift;   // same task as the cancel: one drawn frame, nothing moves
    });
  }

  function drop(to: number): void {   // close
    setHeld(0);
    if (HOLD && held && !run) {   // held: everything is laid out full height already; slide the transforms back to 0
      const hb = hp, hd = held - tk, hs = hShift;
      held = 0; hp = null; hShift = 0; tk = 0; unwatch();
      cur = to; de.removeAttribute(KB_ATTR);
      if (!hb || still) { if (hb) hb.box.concat(kids(hb.sc)).forEach((el) => { el.style.transform = ''; }); unprep(); return; }
      const mv: Array<[HTMLElement | undefined, number[]]> = hb.box.map((el) => [el, DROP.map((k) => -hd * (1 - k))]);
      if (hs > 0) kids(hb.sc).forEach((el) => { mv.push([el, DROP.map((k) => -hs * (1 - k))]); });
      animate(mv, DROP_MS, closeAt ? closeAt : now() - CLOSE_LEAD, () => { stop(); unprep(); }, '');
      return;
    }
    const p = parts();
    let done = 0, inner = 0;
    if (run && p) { done = -offset(p.box[0]); inner = -offset(p.body[0]); }
    stop();
    const d = base + done - to;   // from where the box is drawn (closing while still rising: part way)
    cur = to; tk = 0;
    const sc = p?.sc, s0 = sc ? sc.scrollTop + inner : 0;
    setCover(to);
    if (!p || !sc || d <= 0 || still) return;
    void p.w.offsetHeight;   // lay out at the new height; a list at its end scrolls back by up to d
    const shift = Math.max(0, s0 - sc.scrollTop);   // how far the content would jump down: slide it instead
    const moves: Array<[HTMLElement | undefined, number[]]> = p.box.map((el) => [el, CLOSE.map((k) => -d * (1 - k))]);
    if (shift > 0) p.body.forEach((el) => { moves.push([el, CLOSE.map((k) => -shift * (1 - k))]); });
    prep(p);
    animate(moves, CLOSE_MS, leftAt || now(), () => { stop(); unprep(); });
  }

  function cover(c: number): void {
    if (!RIDE) { cur = c; setCover(c); return; }
    const t = now();
    if (c > 0 && t < closingUntil) { swallowed = c; return; }   // the site's stale "still covered" right after the box lets go
    // a "0", or a value after the window, is news: cancel a pending sit-back even when the message beats its late timer
    // (a slow page ran the timer after the "0" and parked the box above a closed keyboard, trylink rerun 8 Oct 2026)
    if (c === 0 || closingUntil) { closingUntil = 0; swallowed = 0; }
    if (turned && c < KB_UP_MIN_PX) turned = false;
    if (HOLD && turned && !run && c >= KB_UP_MIN_PX && !sideways()) { turned = false; rehold(c); return; }
    if (c === cur) return;
    if (HOLD && held && c > 0 && !run && (Math.abs(c - cur) < SMALL || cur > 0)) { cur = up = held = c; tk = Math.min(c, tuck(hp)); setHeld(held - tk); hRefresh(); hApply(); return; }
    if (Math.abs(c - cur) < SMALL && !run) { cur = c; setCover(c); return; }
    if (cur > 0 && c > 0 && !run) { cur = up = c; setCover(c); return; }   // keyboard already up, its height changed (QuickType, emoji): no slide
    if (c > cur) lift(c); else drop(c);
  }

  // Turned back upright with the keyboard still up: the width change released the hold to the plain
  // layout, and iPhone Safari draws grey below the visible area of a shrunk chat (behind the
  // see-through keyboard bar and around the box). Hold again at once, with no slide: the turn has
  // already redrawn everything. Sideways keeps the plain layout and its tuck (round 2, measured).
  function rehold(c: number): void {
    stop();
    cur = up = c;
    setCover(0);   // HOLD keeps the full height
    const p = parts();
    if (!p || still) { setCover(c); return; }
    tk = Math.min(c, tuck(p));
    prep(p);
    setHeld(c - tk);   // TRAC-1176: the arrow/pill take the box's lift
    hHold(p, c, p.atEnd ? hEnd(p.sc, c - tk) : 0);
  }
  const sideways = (): boolean => typeof win.matchMedia === 'function' && win.matchMedia(LANDSCAPE_PHONE_QUERY).matches;

  function reset(): void {   // back to the plain layout at the latest cover, nothing moving
    stop();
    if (sendRaf) { win.cancelAnimationFrame?.(sendRaf); sendRaf = 0; }
    if (held && hp) { hp.box.forEach((el) => { el.style.transform = ''; }); kids(hp.sc).forEach((el) => { el.style.transform = ''; }); }
    held = 0; hp = null; hShift = 0; tk = 0; turned = false; unwatch(); setHeld(0);
    closingUntil = 0; swallowed = 0; closeAt = 0; leftAt = 0;
    unprep();
    setCover(cur);
  }

  on(doc, 'focusin', (e: FocusEvent) => {
    if (!RIDE || !isTextField(e.target)) return;
    prep(parts());   // the keyboard is coming: get the layers ready now, long before the first slide
    later(() => { if (!cur && !run && !held) unprep(); }, 1500);   // no cover came (a site without gap fix A): take them off again
    if (closingUntil && cur === 0 && up > 0 && run) lift(up);   // back in the box while it slides down: ride back up
    closingUntil = 0;
  }, true);
  on(doc, 'focusout', (e: FocusEvent) => {
    if (!RIDE || !isTextField(e.target) || isTextField(e.relatedTarget) || cur === 0) return;   // focus moving to another field keeps the keyboard
    const t = now(), byPage = t - touchedAt < 400 || t - appBlurAt < 50;
    leftAt = byPage ? t : t - SYS_LEAD;
    closingUntil = t + 1200;
    swallowed = 0;
    if (byPage) {   // a send: the keyboard starts down with the frame that draws the sent message; slide from that frame
      closeAt = 0;
      if (sendRaf) win.cancelAnimationFrame?.(sendRaf);
      sendRaf = win.requestAnimationFrame(() => {
        sendRaf = 0;
        if (!closingUntil || cur === 0) return;   // back in the box already, or the site's "0" came first
        hRefresh();   // the send re-rendered the box: slide the new nodes
        closeAt = now();
        drop(0);
        leftAt = 0; closeAt = 0;
      });
      return;   // a send always takes the keyboard down: a stale "still covered" never lifts the box again
    }
    closeAt = HOLD ? t + CLOSE_WAIT_SYS : 0;
    drop(0);
    leftAt = 0; closeAt = 0;
    later(() => {   // no "0" came: the keyboard stayed up after all, sit back on it
      if (closingUntil && now() >= closingUntil - 5 && swallowed > 0) { closingUntil = 0; stop(); cur = swallowed; setCover(swallowed); }
    }, 1200);
  }, true);
  on(win, KB_APP_BLUR_EVENT, () => { appBlurAt = now(); });
  on(doc, 'touchend', () => { touchedAt = now(); }, { capture: true, passive: true });

  // While held the list's content sits hShift px up, so its first hShift px are above the list's top.
  // Once the person has scrolled away from the end and stopped, that transform becomes scroll (same
  // task, nothing moves), so the whole history is in reach; back at the end it slides up again.
  function idle(): void {
    if (!held || !hp || run || touching) return;
    const sc = hp.sc, max = sc.scrollHeight - sc.clientHeight, atEnd = max - sc.scrollTop < 8;
    if (!atEnd && hShift > 0 && sc.scrollTop + hShift <= max) {
      const s0 = sc.scrollTop, k = hShift; hShift = 0; hApply(); sc.scrollTop = s0 + k;
    } else if (atEnd && hShift === 0) {
      const to = hEnd(sc, held - tk);
      if (to <= 0) { follow = true; return; }
      kids(sc).forEach((el) => { el.animate?.([{ transform: 'translateY(0)' }, { transform: `translateY(${-to}px)` }], { duration: 180, easing: 'ease-out' }); });
      follow = true; hShift = to; hApply();
    }
  }
  const idleSoon = (): void => { clearTimeout(idleT); idleT = later(idle, 160); };
  on(doc, 'scroll', (e: Event) => {
    if (!held || !hp || e.target !== hp.sc || run) return;
    const sc = hp.sc;
    if (sc.scrollHeight - sc.clientHeight - sc.scrollTop < 8) { follow = true; return; }
    follow = false; idleSoon();
  }, true);
  on(doc, 'touchstart', () => { touching = 1; }, { capture: true, passive: true });
  const untouch = (e: TouchEvent): void => { if (!e.touches.length) { touching = 0; idleSoon(); } };
  on(doc, 'touchend', untouch, { capture: true, passive: true });
  on(doc, 'touchcancel', untouch, { capture: true, passive: true });

  on(doc, 'visibilitychange', () => { if (doc.hidden && (run || held)) reset(); });
  on(win, 'resize', () => {   // rotation: the real layout, then wait for the next cover; a height change (Safari's bars): re-measure
    if (win.innerWidth !== lastW) { turned = cur >= KB_UP_MIN_PX; if (held) hRelease(); else if (run) { stop(); unprep(); setCover(cur); } }
    else if (held) hRefresh();
    else if (run && !HOLD) { stop(); setCover(cur); }
    lastW = win.innerWidth;
  });

  let lockStyle: HTMLStyleElement | null = null;
  if (LOCK) {
    // Nothing in the chat pans except what scrolls, and the list does not hand its scroll on to the page.
    lockStyle = doc.createElement('style');
    lockStyle.textContent = `html,body{touch-action:pinch-zoom;overscroll-behavior:none}[${KB_PART_ATTR}="list"],[${KB_PART_ATTR}="list"] *{overscroll-behavior:contain}`;
    (doc.head || de).appendChild(lockStyle);
    // iOS 26 Safari ignores the frame's touch-action for the page's own pan (a drag on the header with
    // the keyboard up scrolled the page behind by up to 590 pt), so a move that starts on nothing
    // scrollable, or pushes a scroller past its end, is cancelled. A sideways drag on a sideways
    // scroller (product cards in an answer) is left alone; so is a drag in a text field (caret, selection).
    let tY = 0, tX = 0, tDir = 0;
    let tSc: HTMLElement | 'text' | null = null, tH: HTMLElement | null = null;
    const scroller = (el: Element | null, x: boolean): HTMLElement | null => {
      for (let e = el as HTMLElement | null; e && e.nodeType === 1 && e !== doc.body && e !== de; e = e.parentElement) {
        const s = css(e);
        if (x ? e.scrollWidth > e.clientWidth + 1 && /(auto|scroll)/.test(s.overflowX)
          : e.scrollHeight > e.clientHeight + 1 && /(auto|scroll)/.test(s.overflowY)) return e;
      }
      return null;
    };
    on(doc, 'touchstart', (e: TouchEvent) => {
      tSc = null; tDir = 0;
      if (e.touches.length !== 1) return;
      tY = e.touches[0].clientY; tX = e.touches[0].clientX;
      const el = e.target as Element | null;
      tH = scroller(el, true);
      const v = scroller(el, false);
      tSc = (isTextField(el) || !!el?.matches?.('input[type=range]')) && !v ? 'text' : v;   // a slider drags itself
    }, { capture: true, passive: true });
    on(doc, 'touchmove', (e: TouchEvent) => {
      if (tSc === 'text' || e.touches.length !== 1 || !e.cancelable) return;
      const x = e.touches[0].clientX, y = e.touches[0].clientY, dy = y - tY;
      if (!tDir && Math.abs(x - tX) + Math.abs(dy) > 6) tDir = Math.abs(x - tX) > Math.abs(dy) ? 1 : 2;   // latch the direction once
      if (tDir === 1 && tH) return;
      tY = y;
      if (!tSc) { e.preventDefault(); return; }
      const top = tSc.scrollTop <= 0, end = tSc.scrollTop + tSc.clientHeight >= tSc.scrollHeight - 1;
      if ((dy > 0 && top) || (dy < 0 && end)) e.preventDefault();
    }, { capture: true, passive: false });
  }

  return {
    cover,
    reset,
    last: () => cur,
    uninstall(): void {
      reset();
      offs.forEach((off) => off());
      timers.forEach((id) => clearTimeout(id));
      timers.clear();
      back.remove();
      lockStyle?.remove();
      de.removeAttribute(KB_ATTR);
      de.style.removeProperty(KB_COVER_VAR);
      de.style.removeProperty(KB_TUCK_VAR);
    },
  };
}
